import * as THREE from 'three';
import type { AssetLibrary } from '../assets/AssetLibrary';
import { createProceduralGrassTexture } from '../assets/AssetLibrary';
import { FLOWER_META, GAME, ZONE, unlockedFlowers, type FlowerKind } from '../config';
import {
  createBushMesh,
  createCounterMesh,
  createFenceSegment,
  createGround,
  createHiveMesh,
  createPathTile,
  createStoreroomMesh,
  createTreeMesh,
  createUpgradeKioskMesh,
} from '../meshes/factory';
import type { Rng } from '../utils/random';
import { pick, randRange } from '../utils/random';

export type Plot = {
  id: number;
  kind: FlowerKind;
  position: THREE.Vector3;
  ready: boolean;
  growTimer: number;
  mesh: THREE.Group;
  reserved: boolean;
  ring: number;
  locked: boolean;
};

export class World {
  readonly root = new THREE.Group();
  readonly plots: Plot[] = [];
  readonly hivePos = new THREE.Vector3(ZONE.hive.x, 0, ZONE.hive.z);
  readonly counterPos = new THREE.Vector3(ZONE.counter.x, 0, ZONE.counter.z);
  readonly storeroomPos = new THREE.Vector3(ZONE.storeroom.x, 0, ZONE.storeroom.z);
  readonly upgradePos = new THREE.Vector3(ZONE.upgradeShop.x, 0, ZONE.upgradeShop.z);
  readonly moneyPad = new THREE.Vector3(ZONE.moneyPad.x, 0, ZONE.moneyPad.z);
  readonly queueAnchor = new THREE.Vector3(ZONE.counter.x, 0, ZONE.counter.z + 2.4);
  readonly customerSpawn = new THREE.Vector3(0, 0, 16);
  bounds = GAME.baseWorldHalf - 1.2;

  private ground: THREE.Mesh | null = null;
  private deco = new THREE.Group();
  private buildingGroup = new THREE.Group();
  private fieldsLevel = 0;
  private flowersLevel = 0;
  private nextPlotId = 0;
  private assets: AssetLibrary | null = null;
  private grassTex: THREE.Texture | null = null;

  constructor(private readonly rng: Rng) {
    this.root.add(this.deco);
    this.root.add(this.buildingGroup);
    this.rebuildStatic(0);
    this.buildAllPlots(0, 0);
  }

  /** Hot-swap in authored Synty (or other) kits after async load. */
  attachAssets(assets: AssetLibrary): void {
    this.assets = assets;
    this.grassTex = createProceduralGrassTexture();
    this.rebuildStatic(this.fieldsLevel);
    // keep plots; only environment/buildings refresh
  }

  applyProgress(fieldsLevel: number, flowersLevel: number, shopLevel: number): void {
    const fieldsChanged = fieldsLevel !== this.fieldsLevel;
    const flowersChanged = flowersLevel !== this.flowersLevel;
    const prevFields = this.fieldsLevel;
    this.fieldsLevel = fieldsLevel;
    this.flowersLevel = flowersLevel;
    this.bounds = GAME.baseWorldHalf + fieldsLevel * 3.5 - 1.2;
    if (fieldsChanged) {
      this.rebuildStatic(shopLevel);
      this.unlockRingsIncremental(prevFields, fieldsLevel);
    } else {
      this.refreshBuildings(shopLevel);
    }
    if (flowersChanged) this.reseedLockedPlots();
  }

  private rebuildStatic(shopLevel: number): void {
    // clear deco + buildings + ground
    while (this.deco.children.length) this.deco.remove(this.deco.children[0]!);
    while (this.buildingGroup.children.length) this.buildingGroup.remove(this.buildingGroup.children[0]!);
    if (this.ground) {
      this.root.remove(this.ground);
      this.ground.geometry.dispose();
    }

    const size = GAME.baseWorldHalf + this.fieldsLevel * 3.5 + 2;
    this.ground = createGround(size);
    if (this.grassTex && this.ground.material instanceof THREE.MeshStandardMaterial) {
      this.ground.material.map = this.grassTex;
      this.ground.material.color.setHex(0xffffff);
      this.ground.material.roughness = 0.95;
      this.ground.material.needsUpdate = true;
    }
    this.root.add(this.ground);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(size - 0.6, size + 0.8, 64),
      new THREE.MeshStandardMaterial({ color: 0x3d8f5c, flatShading: true, roughness: 0.95 }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.01;
    ring.receiveShadow = true;
    this.deco.add(ring);

    // Paths
    this.deco.add(path(3.4, 18, 0, 2));
    this.deco.add(path(18, 2.6, 0, 7.5));
    this.deco.add(path(14, 2.2, 0, -4));
    this.deco.add(path(12, 1.8, 0, -9));
    this.deco.add(path(6, 1.8, 5.5, -1));
    this.deco.add(path(6, 1.8, -5, 5));
    this.deco.add(path(5, 1.8, 5, 6.5));

    this.addPad(this.hivePos, 0xf0b429, 1.8);
    this.addPad(this.storeroomPos, 0x8ec5ff, 2.0);
    this.addPad(this.counterPos, 0xffc4d0, 2.4);
    this.addPad(this.upgradePos, 0xffe08a, 1.7);
    this.addPad(this.moneyPad, 0x7dffb0, 1.3);

    this.refreshBuildings(shopLevel);

    // Outer trees — prefer Synty farm trees when loaded
    const treeCount = 10 + this.fieldsLevel * 2;
    for (let i = 0; i < treeCount; i++) {
      const a = (i / treeCount) * Math.PI * 2;
      const rad = size - 2.4 + randRange(this.rng, -0.5, 0.7);
      const key = i % 3 === 0 ? 'treeLarge' : 'treeApple';
      const synty = this.assets?.clone(key);
      if (synty) {
        synty.position.set(Math.cos(a) * rad, 0, Math.sin(a) * rad);
        synty.rotation.y = this.rng() * Math.PI * 2;
        const sc = 0.85 + this.rng() * 0.35;
        synty.scale.multiplyScalar(sc);
        this.deco.add(synty);
      } else {
        const tree = createTreeMesh(i % 4);
        tree.position.set(Math.cos(a) * rad, 0, Math.sin(a) * rad);
        tree.scale.setScalar(0.75 + this.rng() * 0.55);
        this.deco.add(tree);
      }
    }

    // Synty flower clusters + dirt rows for density
    if (this.assets?.hasAssets) {
      for (let i = 0; i < 8 + this.fieldsLevel * 2; i++) {
        const flower = this.assets.clone(pick(this.rng, ['flowers1', 'flowers2', 'flowers3'] as const));
        if (!flower) continue;
        flower.position.set(randRange(this.rng, -10, 10), 0, randRange(this.rng, -12, 4));
        flower.rotation.y = this.rng() * Math.PI * 2;
        this.deco.add(flower);
      }
      for (let i = 0; i < 4; i++) {
        const rows = this.assets.clone(i % 2 ? 'dirtRows' : 'vegeRows');
        if (!rows) continue;
        rows.position.set(-8 + i * 4.5, 0, -6 - (i % 2) * 2);
        rows.rotation.y = Math.PI / 2;
        this.deco.add(rows);
      }
    }

    // Sparse wildflower accents
    const wild = 18 + this.fieldsLevel * 6;
    for (let i = 0; i < wild; i++) {
      const x = randRange(this.rng, -size + 2, size - 2);
      const z = randRange(this.rng, -size + 2, size - 2);
      if (Math.hypot(x, z - 8) < 3.5) continue;
      const petal = new THREE.Mesh(
        new THREE.SphereGeometry(0.09, 5, 5),
        new THREE.MeshStandardMaterial({
          color: pick(this.rng, [0xe4576a, 0x4f8cff, 0x49c27a, 0xffd66b, 0xb48cff, 0xff6b9d]),
          flatShading: true,
        }),
      );
      petal.position.set(x, 0.08, z);
      petal.castShadow = false;
      this.deco.add(petal);
    }

    // Garden fence arc (short)
    for (let i = 0; i < 6; i++) {
      const fence = createFenceSegment();
      fence.position.set(-4 + i * 1.2, 0, -2.2);
      this.deco.add(fence);
    }
  }

  private refreshBuildings(shopLevel: number): void {
    while (this.buildingGroup.children.length) this.buildingGroup.remove(this.buildingGroup.children[0]!);

    const counter =
      this.assets?.clone('counter') ??
      (() => {
        const c = createCounterMesh(shopLevel);
        return c;
      })();
    counter.position.copy(this.counterPos);
    if (this.assets?.hasAssets) counter.rotation.y = Math.PI;

    const store = this.assets?.clone('shelter') ?? createStoreroomMesh(shopLevel);
    store.position.copy(this.storeroomPos);
    store.rotation.y = Math.PI / 2;

    const hive = this.assets?.clone('beehive') ?? createHiveMesh(shopLevel);
    hive.position.copy(this.hivePos);
    if (this.assets?.hasAssets) {
      // cluster a few hives for readability
      for (let i = 0; i < 2; i++) {
        const extra = this.assets.clone('beehive');
        if (!extra) break;
        extra.position.set(this.hivePos.x + 0.9 + i * 0.7, 0, this.hivePos.z + (i % 2) * 0.6);
        this.buildingGroup.add(extra);
      }
    }

    const kiosk = createUpgradeKioskMesh();
    kiosk.position.copy(this.upgradePos);

    this.buildingGroup.add(counter, store, hive, kiosk);

    // Landmark Synty buildings for AAA silhouette read
    if (this.assets?.hasAssets) {
      const barn = this.assets.clone('barn');
      if (barn) {
        barn.position.set(-11, 0, 9);
        barn.rotation.y = 0.3;
        this.buildingGroup.add(barn);
      }
      const house = this.assets.clone('farmhouse');
      if (house) {
        house.position.set(11, 0, 9.5);
        house.rotation.y = -0.4;
        this.buildingGroup.add(house);
      }
      const silo = this.assets.clone('silo');
      if (silo) {
        silo.position.set(-12.5, 0, 5.5);
        this.buildingGroup.add(silo);
      }
      const tower = this.assets.clone('waterTower');
      if (tower) {
        tower.position.set(12.5, 0, 4);
        this.buildingGroup.add(tower);
      }
      const green = this.assets.clone('greenhouse');
      if (green) {
        green.position.set(9.5, 0, -8);
        green.rotation.y = 0.2;
        this.buildingGroup.add(green);
      }
      for (let i = 0; i < 3; i++) {
        const crate = this.assets.clone('crate');
        if (!crate) break;
        crate.position.set(this.storeroomPos.x + 0.4 + i * 0.55, 0, this.storeroomPos.z + 1.4);
        this.buildingGroup.add(crate);
      }
      const barrel = this.assets.clone('barrel');
      if (barrel) {
        barrel.position.set(this.counterPos.x - 2.2, 0, this.counterPos.z + 0.4);
        this.buildingGroup.add(barrel);
      }
    }
  }

  private addPad(pos: THREE.Vector3, color: number, radius: number): void {
    const pad = new THREE.Mesh(
      new THREE.CircleGeometry(radius, 28),
      new THREE.MeshStandardMaterial({ color, transparent: true, opacity: 0.24, roughness: 1 }),
    );
    pad.rotation.x = -Math.PI / 2;
    pad.position.set(pos.x, 0.04, pos.z);
    this.deco.add(pad);
  }

  private buildAllPlots(fieldsLevel: number, flowersLevel: number): void {
    // clear existing plot meshes
    for (const p of this.plots) {
      this.root.remove(p.mesh);
    }
    this.plots.length = 0;
    this.nextPlotId = 0;
    this.fieldsLevel = fieldsLevel;
    this.flowersLevel = flowersLevel;

    // Ring 0 — starter garden (always unlocked)
    this.gridRing(0, 5, 3, -5.2, -3.2, 2.25, 2.1, false);
    // Ring 1
    this.gridRing(1, 6, 2, -6.8, -9.2, 2.35, 2.15, fieldsLevel < 1);
    // Ring 2
    this.gridRing(2, 6, 2, -7.5, -13.5, 2.4, 2.2, fieldsLevel < 2);
    // Ring 3 side gardens
    this.gridRing(3, 3, 2, 8, -4, 2.2, 2.2, fieldsLevel < 3);
    this.gridRing(3, 3, 2, -10.5, -4, 2.2, 2.2, fieldsLevel < 3);
    // Ring 4 far orchard
    this.gridRing(4, 5, 2, -5.5, -17.5, 2.4, 2.25, fieldsLevel < 4);
    // Ring 5 crystal grove
    this.gridRing(5, 4, 2, 6, -15.5, 2.35, 2.2, fieldsLevel < 5);
  }

  private gridRing(
    ring: number,
    cols: number,
    rows: number,
    originX: number,
    originZ: number,
    stepX: number,
    stepZ: number,
    locked: boolean,
  ): void {
    const kinds = unlockedFlowers(this.flowersLevel);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const kind = kinds[(c + r + ring) % kinds.length]!;
        const x = originX + c * stepX + (r % 2) * 0.35;
        const z = originZ - r * stepZ;
        const ready = !locked && this.rng() > 0.28;
        this.spawnPlot(kind, x, z, ready, ring, locked);
      }
    }
  }

  /** Unlock newly purchased rings without wiping existing garden state. */
  private unlockRingsIncremental(prevFields: number, fieldsLevel: number): void {
    if (this.plots.length === 0) {
      this.buildAllPlots(fieldsLevel, this.flowersLevel);
      return;
    }
    for (const plot of this.plots) {
      if (!plot.locked) continue;
      if (plot.ring <= fieldsLevel) {
        plot.locked = false;
        plot.ready = this.rng() > 0.2;
        plot.growTimer = plot.ready ? 0 : 3 + this.rng() * 3;
        this.root.remove(plot.mesh);
        plot.mesh = createBushMesh(plot.kind, plot.ready);
        plot.mesh.position.copy(plot.position);
        // pop-in scale for reveal juice
        plot.mesh.scale.setScalar(0.2);
        this.root.add(plot.mesh);
      }
    }
    // If we jumped multiple levels or plots missing for new rings, fill gaps
    for (let ring = prevFields + 1; ring <= fieldsLevel; ring++) {
      const hasRing = this.plots.some((p) => p.ring === ring);
      if (!hasRing) {
        // append ring layout unlocked
        if (ring === 1) this.gridRing(1, 6, 2, -6.8, -9.2, 2.35, 2.15, false);
        if (ring === 2) this.gridRing(2, 6, 2, -7.5, -13.5, 2.4, 2.2, false);
        if (ring === 3) {
          this.gridRing(3, 3, 2, 8, -4, 2.2, 2.2, false);
          this.gridRing(3, 3, 2, -10.5, -4, 2.2, 2.2, false);
        }
        if (ring === 4) this.gridRing(4, 5, 2, -5.5, -17.5, 2.4, 2.25, false);
        if (ring === 5) this.gridRing(5, 4, 2, 6, -15.5, 2.35, 2.2, false);
      }
    }
  }

  private reseedLockedPlots(): void {
    const kinds = unlockedFlowers(this.flowersLevel);
    for (const plot of this.plots) {
      if (plot.locked) continue;
      // chance to convert empty/growing to newly unlocked kinds if underrepresented
      if (!plot.ready && plot.growTimer <= 0) {
        plot.kind = pick(this.rng, kinds);
      }
    }
  }

  spawnPlot(
    kind: FlowerKind,
    x: number,
    z: number,
    ready: boolean,
    ring: number,
    locked: boolean,
  ): Plot {
    // Locked plots: cheap translucent pad instead of full bush mesh
    let mesh: THREE.Group;
    if (locked) {
      mesh = new THREE.Group();
      const pad = new THREE.Mesh(
        new THREE.CircleGeometry(0.55, 10),
        new THREE.MeshStandardMaterial({
          color: 0x88aa88,
          transparent: true,
          opacity: 0.35,
          flatShading: true,
        }),
      );
      pad.rotation.x = -Math.PI / 2;
      pad.position.y = 0.05;
      mesh.add(pad);
      mesh.position.set(x, 0, z);
    } else {
      mesh = createBushMesh(kind, ready);
      mesh.position.set(x, 0, z);
    }
    this.root.add(mesh);
    const plot: Plot = {
      id: this.nextPlotId++,
      kind,
      position: new THREE.Vector3(x, 0, z),
      ready: ready && !locked,
      growTimer: ready || locked ? 0 : 4 + this.rng() * 4,
      mesh,
      reserved: false,
      ring,
      locked,
    };
    this.plots.push(plot);
    return plot;
  }

  refreshPlotMesh(plot: Plot): void {
    this.root.remove(plot.mesh);
    plot.mesh.traverse((c) => {
      if (c instanceof THREE.Mesh) c.geometry?.dispose();
    });
    plot.mesh = createBushMesh(plot.kind, plot.ready);
    plot.mesh.position.copy(plot.position);
    if (!plot.ready) plot.mesh.scale.setScalar(0.45 + (1 - Math.min(Math.max(plot.growTimer, 0), 6) / 6) * 0.4);
    this.root.add(plot.mesh);
  }

  plantAtEmpty(kind: FlowerKind): Plot | null {
    const open = this.plots.filter((p) => !p.locked && !p.ready && p.growTimer <= 0 && !p.reserved);
    let empty = open[0];
    if (!empty) {
      // grow into unlocked ring space near garden
      const kinds = unlockedFlowers(this.flowersLevel);
      if (!kinds.includes(kind)) kind = kinds[0]!;
      const angle = this.rng() * Math.PI * 2;
      const rad = 4 + this.rng() * (5 + this.fieldsLevel * 1.5);
      const x = Math.cos(angle) * rad * 0.8;
      const z = ZONE.gardenCenter.z + Math.sin(angle) * rad * 0.55 - this.fieldsLevel;
      empty = this.spawnPlot(kind, x, z, false, Math.min(this.fieldsLevel, 5), false);
      empty.growTimer = 4.5 + this.rng() * 2.5;
      return empty;
    }
    empty.kind = kind;
    empty.ready = false;
    empty.growTimer = 4.5 + this.rng() * 2.5;
    empty.mesh.visible = true;
    this.refreshPlotMesh(empty);
    return empty;
  }

  harvestPlot(plot: Plot): FlowerKind | null {
    if (!plot.ready || plot.locked) return null;
    const kind = plot.kind;
    plot.ready = false;
    plot.growTimer = -1;
    plot.mesh.visible = false;
    return kind;
  }

  update(dt: number): void {
    for (const plot of this.plots) {
      // animate unlock pop-in
      if (!plot.locked && plot.mesh.scale.x < 0.99 && plot.ready) {
        const s = Math.min(1, plot.mesh.scale.x + dt * 2.5);
        plot.mesh.scale.setScalar(s);
      }
      if (plot.locked || plot.ready) continue;
      if (plot.growTimer < 0) {
        plot.growTimer -= dt;
        if (plot.growTimer < -10) {
          // natural regrow prefers unlocked catalog
          const kinds = unlockedFlowers(this.flowersLevel);
          plot.kind = this.rng() < 0.7 ? plot.kind : pick(this.rng, kinds);
          if (!kinds.includes(plot.kind)) plot.kind = kinds[0]!;
          plot.growTimer = 5 + this.rng() * 4;
          plot.mesh.visible = true;
          this.refreshPlotMesh(plot);
        }
        continue;
      }
      if (plot.growTimer > 0) {
        plot.growTimer -= dt;
        const t = 1 - Math.min(plot.growTimer, 6) / 6;
        plot.mesh.visible = true;
        plot.mesh.scale.setScalar(0.4 + t * 0.6);
        if (plot.growTimer <= 0) {
          plot.ready = true;
          plot.growTimer = 0;
          this.refreshPlotMesh(plot);
        }
      }
    }
  }

  clampPosition(pos: THREE.Vector3): void {
    const r = this.bounds;
    pos.x = THREE.MathUtils.clamp(pos.x, -r, r);
    pos.z = THREE.MathUtils.clamp(pos.z, -r, r);
  }

  pickPlantKind(rng: Rng): FlowerKind {
    const kinds = unlockedFlowers(this.flowersLevel);
    const readyCounts = new Map<FlowerKind, number>();
    for (const k of kinds) readyCounts.set(k, 0);
    for (const p of this.plots) {
      if (p.ready && !p.locked) readyCounts.set(p.kind, (readyCounts.get(p.kind) ?? 0) + 1);
    }
    let best = kinds[0]!;
    let bestN = Infinity;
    for (const k of kinds) {
      const n = readyCounts.get(k) ?? 0;
      if (n < bestN) {
        bestN = n;
        best = k;
      }
    }
    if (rng() < 0.22) return pick(rng, kinds);
    // slight bias to higher tier when unlocked
    if (rng() < 0.15) {
      const premium = kinds.filter((k) => FLOWER_META[k].tier >= 3);
      if (premium.length) return pick(rng, premium);
    }
    return best;
  }
}

function path(w: number, d: number, x: number, z: number): THREE.Mesh {
  const m = createPathTile(w, d);
  m.position.set(x, 0.03, z);
  return m;
}
